import { useState, FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  AuthShell,
  AuthError,
  GoogleSlot,
  authFieldClass,
  authLabelClass,
  authPrimaryButtonClass,
  authGhostButtonClass,
} from '@/components/auth/AuthShell';
import { useValidatedSession } from '@/components/auth/use-validated-session';

/**
 * Create an account with an emailed sign-in link.
 *
 * No password: redeeming the link both proves the address and signs them in.
 * The name and the intent they arrived with (which page, which regatta's boat
 * they were claiming) travel with the link, because it may be opened in a
 * different browser or on a different device.
 */
export function SignupPage() {
  const { isAuthenticated } = useAuthStore();
  const sessionChecked = useValidatedSession();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const claimSlug = searchParams.get('claim');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Don't act on a persisted session until we know the token still works —
  // otherwise a stale one bounces you somewhere you can't use.
  if (!sessionChecked) return <AuthShell title="Create your account">{null}</AuthShell>;

  if (isAuthenticated) {
    return <Navigate to={redirectTo || '/onboarding'} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.post('/auth/magic-link', {
        email: email.trim(),
        name: name.trim(),
        tosAccepted: true,
        ...(redirectTo ? { redirectTo } : {}),
        ...(claimSlug ? { claimRegattaId: claimSlug } : {}),
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your sign-in link');
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={
          <>
            We sent a sign-in link to
            <br />
            <strong className="text-viewer-ink">{email.trim()}</strong>
          </>
        }
      >
        <p className="text-center text-[12.5px] text-viewer-faint">
          The link expires in 30 minutes.
        </p>
        <button
          onClick={() => {
            setSent(false);
            setEmail('');
            setError(null);
          }}
          className={`${authGhostButtonClass} mt-5`}
        >
          Use a different email
        </button>
      </AuthShell>
    );
  }

  const canSubmit = !!name.trim() && !!email.trim() && tosAccepted;

  return (
    <AuthShell
      title="Create your account"
      subtitle={
        claimSlug ? 'Claim your boat and replay your races.' : 'Replay your races, claim your boat.'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-3.5" noValidate>
        <div>
          <label htmlFor="signup-name" className={authLabelClass}>
            Full name
          </label>
          <input
            id="signup-name"
            className={authFieldClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            autoComplete="name"
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="signup-email" className={authLabelClass}>
            Email
          </label>
          <input
            id="signup-email"
            type="email"
            className={authFieldClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yacht.club"
            autoComplete="email"
            required
          />
          <p className="mt-1 text-[11px] text-viewer-faint">
            We’ll email you a link to sign in. No password to remember.
          </p>
        </div>

        <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
          <input
            type="checkbox"
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-viewer-line2/50 bg-viewer-panel accent-brand-dark"
          />
          <span className="text-[11.5px] leading-snug text-viewer-muted">
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-viewer-accent hover:underline">
              Terms of service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" className="text-viewer-accent hover:underline">
              Privacy policy
            </Link>
            .
          </span>
        </label>

        <AuthError message={error} />

        <button type="submit" className={authPrimaryButtonClass} disabled={!canSubmit || submitting}>
          {submitting ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      </form>

      <GoogleSlot />

      <p className="mt-4 text-center text-[12.5px] text-viewer-muted">
        Already have an account?{' '}
        <Link
          to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'}
          className="font-semibold text-viewer-accent hover:underline"
        >
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
